import { css } from "styled-components/macro";

import { colors } from "UI/colors";

/**
 * Shared styling for the verification-chip family (USPS Verified / Unverified /
 * Admin Verified) so the three pills can't drift. Two fragments because the
 * interactive badge splits chrome and box across two elements (an FnrShineButton
 * sits between them); the static pills apply both to a single span. Change a
 * chip dimension here once and every pill follows — there is no per-pill copy of
 * these values to keep in sync.
 *
 * Consumers: [`UspsVerifiedPill`](./UspsVerifiedPill.jsx),
 * [`StaticPill`](./StaticPill.jsx),
 * and [`VerificationBadge`](../../ExtendedFieldInputs/InputSwitch/AddressInput/VerificationBadge.jsx).
 * The label typography is the separately-shared [`ChipLabel`](./ChipLabel.jsx).
 */

// Pill chrome: the standardization-notice blues + pill shape. `font-size` is the
// em base FnrContainedIcon scales from AND — on the interactive badge — pins the
// FnrShineButton block wrappers between the chrome and the box to 12px, so their
// strut adds no line-box leading (which otherwise made the badge 2px taller than
// the static pills).
//
// A fixed height is the family's single source of height parity: the three
// states are built from structurally different innards (the interactive states
// wrap a FnrShineButton whose 12px strut sets their height; the USPS state wraps
// the eagle in FnrContainedIcon, which reserves ~16px for its icon slot). Left to
// their content they'd differ by ~4px. Pinning the chrome's height (border-box)
// makes all three identical; `align-items: center` centers each state's content
// and `overflow: visible` lets the eagle's taller-than-content icon slot spill
// without clipping (its ink fits within the height, so nothing visible is cut).
//
// `gap: 4px` is the icon→label spacing for the USPS pill, whose padding-free icon
// and label are direct flex children of this chrome span. The other pills nest
// their icon+label inside a row (StaticPill's PillRow, the badge's SlideRow) and
// own a tighter gap there, so this gap only reaches the USPS pill's two children.
export const chipChrome = css`
  display: inline-flex;
  align-items: center;
  box-sizing: border-box;
  height: 16px;
  overflow: visible;
  border-radius: 999px;
  font-size: 12px;
  background: ${colors.fnBlue100};
  border: 1px solid ${colors.fnBlue200};
  gap: 4px;
`;

// The icon + label box. Symmetric 4px left/right padding — the box owns both
// insets now that the icon no longer carries its own padded container (the USPS
// pill passes `noPadding`), so the chip can't rely on container slack for the
// left inset. `inline-flex` so the chip shrink-wraps its content. This is the
// SINGLE source of the chip's box insets: the badge must not add its own padding
// on an inner row, or it drifts from the static pills.
export const chipBox = css`
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  padding: 1px 4px 1px 4px;
`;
